import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  GitBranch,
  RefreshCw,
  X,
  FileText,
  Eye,
  Download,
  Calendar,
  User,
  Clock,
  FileCheck,
  AlertTriangle,
  Trash2,
  Edit,
  Plus,
  Save,
  CheckCircle,
  AlertCircle,
  TrendingUp,
  DollarSign,
  Users,
  MapPin,
  FileBarChart,
  Brain,
  MessageSquare,
  Zap,
  BarChart3,
  HelpCircle,
} from 'lucide-react';
import DataTable from '../common/DataTable';
import ConfirmationModal from '../common/ConfirmationModal';
import PageBreadcrumb from '../common/PageBreadcrumb';
import PageHeader from '../common/PageHeader';
import { useHelp } from '../../context/HelpContext';
import PageHelp from '../help/PageHelp';
import ContractService from '../../services/contractService';
import { useToast, ToastContainer } from '../common/Toast';

const ContractLifecycle = () => {
  const navigate = useNavigate();
  const { openPageHelp } = useHelp();
  const { success, error: showError, toasts, removeToast } = useToast();
  const [activeTab, setActiveTab] = useState('lifecycle');
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showRenewalModal, setShowRenewalModal] = useState(false);
  const [showTerminationModal, setShowTerminationModal] = useState(false);
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [selectedContract, setSelectedContract] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [contracts, setContracts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [renewalAlerts, setRenewalAlerts] = useState([]);
  const [terminatedContracts, setTerminatedContracts] = useState([]);
  const [analyticsData, setAnalyticsData] = useState(null);
  const [loadingRenewals, setLoadingRenewals] = useState(true);
  const [loadingTerminations, setLoadingTerminations] = useState(true);
  const [loadingAnalytics, setLoadingAnalytics] = useState(true);
  const [renewalActionLoading, setRenewalActionLoading] = useState(false);
  const [activeRenewal, setActiveRenewal] = useState(null);

  // Transform backend contract data to frontend format
  const transformContract = (contract) => {
    return {
      id: contract.contractId || contract.id,
      contractId: contract.contractId || contract.id,
      contractTitle: contract.title || contract.contractTitle || '',
      type: contract.type || '',
      status: contract.status || 'Draft',
      priority: contract.priority || 'Medium',
      currentVersion: contract.version || '1.0',
      startDate: contract.startDate
        ? new Date(contract.startDate).toISOString().split('T')[0]
        : '',
      endDate: contract.endDate
        ? new Date(contract.endDate).toISOString().split('T')[0]
        : '',
      renewalDate: contract.renewalDate
        ? new Date(contract.renewalDate).toISOString().split('T')[0]
        : null,
      terminationDate: contract.terminationDate
        ? new Date(contract.terminationDate).toISOString().split('T')[0]
        : null,
      autoRenewal: contract.autoRenewal || false,
      noticePeriod: contract.noticePeriod || 30,
      value: contract.amount || contract.value || 0,
      currency: contract.currency || 'USD',
      counterparty: contract.counterparty || '',
      counterpartyContact:
        contract.counterpartyContact?.email ||
        contract.counterpartyContact ||
        '',
      legalCounsel: contract.legalCounsel || '',
      businessOwner: contract.businessOwner || '',
      lastModified:
        contract.updatedAt || contract.lastModified || new Date().toISOString(),
      modifiedBy:
        contract.updatedBy?.firstName || contract.modifiedBy || 'System',
      lifecycleStage: contract.status || 'Active',
      riskLevel: contract.riskLevel || 'Medium',
      complianceStatus: contract.complianceStatus || 'Compliant',
      nextReviewDate: contract.nextReviewDate
        ? new Date(contract.nextReviewDate).toISOString().split('T')[0]
        : null,
      terminationClause: contract.terminationClause || '',
      renewalTerms: contract.renewalTerms || '',
      keyTerms: contract.keyTerms || '',
      documents: contract.documents || [],
      notes: contract.notes || '',
    };
  };

  // Load contracts from backend
  const loadContracts = async () => {
    setLoading(true);
    try {
      const response = await ContractService.getContracts({
        includeInactive: false,
      });

      const contractsData = response.data || response || [];
      const transformedContracts = contractsData.map(transformContract);

      setContracts(transformedContracts);
    } catch (error) {
      console.error('Error loading contracts:', error);
      showError('Failed to load contracts. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const loadRenewals = async () => {
    setLoadingRenewals(true);
    try {
      const response = await ContractService.getRenewalAlerts({
        status: 'pending',
      });
      const payload = response?.data || response || [];
      setRenewalAlerts(Array.isArray(payload) ? payload : []);
    } catch (error) {
      console.error('Error loading renewals:', error);
      showError('Failed to load renewal alerts');
    } finally {
      setLoadingRenewals(false);
    }
  };

  const loadTerminations = async () => {
    setLoadingTerminations(true);
    try {
      const response = await ContractService.getTerminatedContracts();
      const payload = response?.data || response || [];
      setTerminatedContracts(Array.isArray(payload) ? payload : []);
    } catch (error) {
      console.error('Error loading terminations:', error);
      showError('Failed to load terminated contracts');
    } finally {
      setLoadingTerminations(false);
    }
  };

  const loadAnalytics = async () => {
    setLoadingAnalytics(true);
    try {
      const response = await ContractService.getContractAnalytics();
      const payload = response?.data || response || null;
      setAnalyticsData(payload);
    } catch (error) {
      console.error('Error loading analytics:', error);
      showError('Failed to load lifecycle analytics');
    } finally {
      setLoadingAnalytics(false);
    }
  };

  const getRenewalExpiry = (alert) => {
    let baseDate = new Date();
    if (alert?.contract?.endDate) {
      baseDate = new Date(alert.contract.endDate);
    } else if (alert?.expiryDate) {
      baseDate = new Date(alert.expiryDate);
    }
    if (Number.isNaN(baseDate.getTime())) {
      baseDate = new Date();
    }
    const nextExpiry = new Date(baseDate);
    nextExpiry.setFullYear(nextExpiry.getFullYear() + 1);
    return nextExpiry.toISOString().split('T')[0];
  };

  const resolveRenewalAlert = (row) => {
    return (
      renewalAlerts.find(
        (alert) => (alert._id || alert.id) === row.alertId
      ) || {
        _id: row.alertId,
        contractId: row.contractId,
        expiryDate: row.expiryDate,
        contract: {
          contractId: row.contractId,
          endDate: row.expiryDate,
        },
      }
    );
  };

  const handleRenewalClick = (alert) => {
    setActiveRenewal(alert);
    setShowRenewalModal(true);
  };

  const confirmRenewal = async () => {
    if (!activeRenewal || !activeRenewal.contract?.contractId) {
      return;
    }
    setRenewalActionLoading(true);
    try {
      await ContractService.renewContract(activeRenewal.contract.contractId, {
        alertId: activeRenewal.alertId || activeRenewal._id,
        nextExpiryDate: getRenewalExpiry(activeRenewal),
        notes: 'Renewal confirmed from lifecycle management',
      });
      success('Contract renewed successfully');
      setShowRenewalModal(false);
      setActiveRenewal(null);
      await Promise.all([
        loadContracts(),
        loadRenewals(),
        loadTerminations(),
        loadAnalytics(),
      ]);
    } catch (error) {
      console.error('Error confirming renewal:', error);
      showError('Failed to renew contract. Please try again.');
    } finally {
      setRenewalActionLoading(false);
    }
  };

  useEffect(() => {
    loadContracts();
    loadRenewals();
    loadTerminations();
    loadAnalytics();
  }, []);

  // Calculate dynamic statistics
  const stats = {
    totalContracts: analyticsData?.totalContracts ?? contracts.length,
    activeContracts:
      analyticsData?.activeContracts ??
      contracts.filter(
        (c) =>
          c.status === 'Active' ||
          c.status === 'APPROVED' ||
          c.status === 'EXECUTED'
      ).length,
    pendingRenewal: renewalAlerts.filter(
      (alert) => alert.alertStatus === 'pending'
    ).length,
    totalValue:
      analyticsData?.totalValue ??
      contracts.reduce((sum, c) => sum + (c.value || 0), 0),
    renewalRate: analyticsData?.renewalRate ?? 0,
    terminationRate: analyticsData?.terminationRate ?? 0,
    renewedContracts: analyticsData?.renewedContracts ?? 0,
    terminatedContracts: analyticsData?.terminatedContracts ?? 0,
  };

  // Filtered contracts
  const filteredContracts = contracts.filter((contract) => {
    const matchesSearch =
      searchTerm === '' ||
      contract.contractTitle
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase()) ||
      contract.contractId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contract.counterparty?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      contract.id?.toLowerCase().includes(searchTerm.toLowerCase());

    return matchesSearch;
  });

  const statusBreakdown = Object.entries(analyticsData?.statusCounts || {});
  const typeBreakdown = Object.entries(analyticsData?.typeCounts || {});

  const tabs = [
    { id: 'lifecycle', label: 'Contract Lifecycle', icon: GitBranch },
    { id: 'renewals', label: 'Renewals', icon: RefreshCw },
    { id: 'terminations', label: 'Terminations', icon: X },
    { id: 'analytics', label: 'Analytics', icon: BarChart3 },
  ];

  // Define columns for DataTable
  const columns = [
    {
      key: 'id',
      label: 'Contract ID',
      width: '150px',
      render: (value, row) => (
        <div className="d-flex align-items-center gap-2">
          <FileText size={16} style={{ color: 'var(--primary-color)' }} />
          <strong style={{ color: 'var(--text-primary)' }}>{value || row.contractId || '—'}</strong>
        </div>
      )
    },
    {
      key: 'contractTitle',
      label: 'Contract',
      width: '250px',
      render: (value, row) => (
        <div>
          <div className="fw-medium">{row.contractId}</div>
          <div className="text-muted" style={{ fontSize: '12px' }}>
            {value}
          </div>
        </div>
      ),
    },
    {
      key: 'type',
      label: 'Type',
      width: '100px',
      render: (type) => (
        <span
          className={`badge ${
            type === 'Lease'
              ? 'badge-primary'
              : type === 'Service'
              ? 'badge-warning'
              : type === 'License'
              ? 'badge-success'
              : type === 'Insurance'
              ? 'badge-danger'
              : 'badge-info'
          }`}
        >
          {type}
        </span>
      ),
    },
    {
      key: 'status',
      label: 'Status',
      width: '120px',
      render: (status) => (
        <span
          className={`badge ${
            status === 'Active' ||
            status === 'APPROVED' ||
            status === 'EXECUTED'
              ? 'badge-success'
              : status === 'Pending Renewal' || status === 'IN_REVIEW'
              ? 'badge-warning'
              : status === 'Expired' || status === 'EXPIRED'
              ? 'badge-danger'
              : 'badge-primary'
          }`}
        >
          {status}
        </span>
      ),
    },
    {
      key: 'counterparty',
      label: 'Counterparty',
      width: '180px',
    },
    {
      key: 'endDate',
      label: 'End Date',
      width: '120px',
      render: (date) => (date ? new Date(date).toLocaleDateString() : '-'),
    },
    {
      key: 'value',
      label: 'Value',
      width: '120px',
      render: (value, row) => (
        <div className="d-flex align-items-center gap-1">
          <DollarSign size={14} style={{ color: '#10b981' }} />
          <span className="fw-medium">${(value || 0).toLocaleString()}</span>
        </div>
      ),
    },
    {
      key: 'riskLevel',
      label: 'Risk',
      width: '100px',
      render: (risk) => (
        <span
          className={`badge ${
            risk === 'Critical' || risk === 'critical'
              ? 'badge-danger'
              : risk === 'High' || risk === 'high'
              ? 'badge-warning'
              : risk === 'Medium' || risk === 'medium'
              ? 'badge-primary'
              : 'badge-success'
          }`}
        >
          {risk}
        </span>
      ),
    },
  ];

  const renewalRows = renewalAlerts.map((alert) => {
    const contract = alert.contract || {};
    const expiryDate = alert.expiryDate || contract.endDate;
    const expiry = expiryDate ? new Date(expiryDate) : null;
    const daysRemaining = expiry
      ? Math.ceil((expiry - new Date()) / (1000 * 60 * 60 * 24))
      : null;
    return {
      id: alert._id || alert.id,
      alertId: alert._id || alert.id,
      contractId: contract.contractId || alert.contractId,
      title: contract.title || '',
      counterparty: contract.counterparty || '',
      expiryDate,
      daysRemaining,
      alertStatus: alert.alertStatus || 'pending',
      autoRenew: alert.autoRenew ? 'Yes' : 'No',
      nextAlertAt: alert.nextAlertAt,
      amount: contract.amount || 0,
    };
  });

  const renewalColumns = [
    {
      key: 'contractId',
      label: 'Contract ID',
      width: '140px',
    },
    {
      key: 'title',
      label: 'Contract',
      width: '220px',
    },
    {
      key: 'counterparty',
      label: 'Counterparty',
      width: '180px',
    },
    {
      key: 'expiryDate',
      label: 'Expiry Date',
      width: '120px',
      render: (date) => (date ? new Date(date).toLocaleDateString() : '—'),
    },
    {
      key: 'daysRemaining',
      label: 'Days Left',
      width: '110px',
      render: (value) => (value !== null && value !== undefined ? value : '—'),
    },
    {
      key: 'alertStatus',
      label: 'Status',
      width: '120px',
      render: (status) => (
        <span
          className={`badge ${
            status === 'pending' ? 'badge-warning' : 'badge-success'
          }`}
        >
          {status}
        </span>
      ),
    },
    {
      key: 'autoRenew',
      label: 'Auto Renew',
      width: '110px',
    },
  ];

  const terminationRows = terminatedContracts.map((contract) => ({
    id: contract.contractId || contract.id,
    contractId: contract.contractId || contract.id,
    title: contract.title || '',
    counterparty: contract.counterparty || '',
    terminatedAt: contract.terminatedAt || contract.updatedAt,
    terminationReason: contract.terminationReason || '—',
    status: contract.status || 'Terminated',
    amount: contract.amount || 0,
  }));

  const terminationColumns = [
    {
      key: 'contractId',
      label: 'Contract ID',
      width: '140px',
    },
    {
      key: 'title',
      label: 'Contract',
      width: '220px',
    },
    {
      key: 'counterparty',
      label: 'Counterparty',
      width: '180px',
    },
    {
      key: 'terminatedAt',
      label: 'Terminated On',
      width: '140px',
      render: (value) => (value ? new Date(value).toLocaleDateString() : '—'),
    },
    {
      key: 'terminationReason',
      label: 'Reason',
      width: '220px',
    },
    {
      key: 'status',
      label: 'Status',
      width: '120px',
      render: (status) => <span className="badge badge-danger">{status}</span>,
    },
  ];

  // Handler functions
  const handleView = (contract) => {
    navigate(`/contracts/view/${contract.contractId || contract.id}`);
  };

  const handleEdit = (contract) => {
    navigate(`/contracts/edit/${contract.contractId || contract.id}`);
  };

  const handleDelete = (contract) => {
    setSelectedContract(contract);
    setShowDeleteModal(true);
  };

  const handleAddNew = () => {
    navigate('/contracts/management');
  };

  const handleDeleteConfirm = async () => {
    setIsDeleting(true);
    try {
      if (selectedContract?.contractId || selectedContract?.id) {
        await ContractService.deleteContract(
          selectedContract.contractId || selectedContract.id
        );
        success('Contract deleted successfully');
        await Promise.all([
          loadContracts(),
          loadTerminations(),
          loadRenewals(),
          loadAnalytics(),
        ]);
      }

      setShowDeleteModal(false);
      setSelectedContract(null);
    } catch (error) {
      console.error('Error deleting contract:', error);
      showError('Failed to delete contract. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleRefresh = () => {
    loadContracts();
    loadRenewals();
    loadTerminations();
    loadAnalytics();
  };

  // Header actions
  const headerActions = [
    {
      label: '',
      icon: <RefreshCw size={18} className={loading ? 'spinning' : ''} />,
      onClick: handleRefresh,
      variant: 'outline',
      tooltip: 'Refresh contracts',
      disabled: loading,
    },
    {
      label: 'New Contract',
      icon: <Plus size={18} />,
      onClick: handleAddNew,
      variant: 'primary',
    },
    {
      label: '',
      icon: <HelpCircle size={18} />,
      onClick: () => openPageHelp('contract-lifecycle'),
      variant: 'outline',
      tooltip: 'Help',
    },
  ];

  // KPI card icon configurations matching Dashboard
  const kpiCards = [
    {
      value: stats.totalContracts,
      label: 'Total Contracts',
      icon: FileText,
      iconColor: '#2D7DD2',
      iconBg: '#D6EFFF',
    },
    {
      value: stats.activeContracts,
      label: 'Active Contracts',
      icon: CheckCircle,
      iconColor: '#059669',
      iconBg: '#D1FAE5',
    },
    {
      value: stats.pendingRenewal,
      label: 'Pending Renewals',
      icon: RefreshCw,
      iconColor: '#F59E0B',
      iconBg: '#FEF3C7',
    },
    {
      value: `$${(stats.totalValue || 0).toLocaleString()}`,
      label: 'Portfolio Value',
      icon: DollarSign,
      iconColor: '#7C3AED',
      iconBg: '#EDE9FE',
    },
    {
      value: `${stats.renewalRate}%`,
      label: 'Renewal Rate',
      icon: TrendingUp,
      iconColor: '#059669',
      iconBg: '#D1FAE5',
    },
    {
      value: `${stats.terminationRate}%`,
      label: 'Termination Rate',
      icon: AlertTriangle,
      iconColor: '#DC2626',
      iconBg: '#FEE2E2',
    },
  ];

  return (
    <>
      <PageBreadcrumb />
      <PageHeader
        title="Contract Lifecycle Management"
        description="Manage contract versioning, renewals, terminations, and lifecycle events"
        actions={headerActions}
      />

      <div
        className="page-container"
        style={{
          width: '100%',
          maxWidth: '100%',
          paddingLeft: '24px',
          paddingRight: '24px',
        }}
      >
        {/* KPI Cards – StatCard reference styling (object-driven) */}
        <div className="grid-modern-4 w-full gap-6 mb-8">
          {kpiCards.map((card, index) => {
            const IconComponent = card.icon;

            return (
              <div
                key={index}
                className="relative border border-[var(--border-color)] bg-white"
              >
                {/* Top Accent Line */}
                <div className="h-1 w-full bg-[var(--primary-color)]" />

                {/* Content */}
                <div className="relative flex items-center gap-3 p-4">
                  {/* Icon */}
                  <div
                    className="flex h-11 w-11 shrink-0 items-center justify-center"
                    style={{ background: card.iconBg }}
                  >
                    <IconComponent
                      size={20}
                      style={{ color: card.iconColor }}
                    />
                  </div>

                  {/* Text */}
                  <div>
                    <div className="text-[20px] font-bold text-[var(--text-primary)]">
                      {card.value}
                    </div>
                    <div className="mt-1 text-[13px] text-[var(--text-secondary)]">
                      {card.label}
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* Tabbed Navigation */}
        <div className="mt-8 mb-8">
          <div className="flex bg-white border-b border-slate-200">
            {tabs.map((tab) => {
              const IconComponent = tab.icon;
              const isActive = activeTab === tab.id;

              return (
                <button
                  key={tab.id}
                  type="button"
                  onClick={() => setActiveTab(tab.id)}
                  className={`
            flex items-center justify-center gap-2 px-5 py-3 w-full
            text-sm font-medium
            border-b-2 transition-colors duration-200
            ${
              isActive
                ? 'border-[var(--primary-color)] text-[var(--primary-color)] bg-white'
                : 'border-transparent text-slate-500 hover:bg-slate-100'
            }
          `}
                >
                  <IconComponent
                    size={18}
                    className={
                      isActive
                        ? 'text-[var(--primary-color)]'
                        : 'text-slate-400'
                    }
                  />
                  <span className="whitespace-nowrap">{tab.label}</span>
                </button>
              );
            })}
          </div>
        </div>

        {/* Main Content - Tab Content */}
        <div style={{ marginTop: '32px' }}>
          {/* Lifecycle Tab - DataTable */}
          {activeTab === 'lifecycle' && (
            <div className="page-tab-content">
              {loading ? (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: '400px',
                    flexDirection: 'column',
                    gap: '16px',
                    backgroundColor: 'white',
                    border: '1px solid var(--border-color)',
                    padding: '40px',
                  }}
                >
                  <div className="loading-spinner"></div>
                  <p
                    style={{
                      color: 'var(--text-secondary)',
                      fontFamily: 'var(--font-family)',
                    }}
                  >
                    Loading contracts...
                  </p>
                </div>
              ) : (
                <div
                  className="page-table-container"
                  style={{
                    backgroundColor: 'white',
                    border: '1px solid var(--border-color)',
                  }}
                >
                  <DataTable
                    data={filteredContracts}
                    columns={columns}
                    title="Contract Lifecycle Records"
                    searchable={true}
                    sortable={true}
                    exportable={true}
                    pagination={true}
                    pageSize={10}
                    showActions={true}
                    showCheckboxes={false}
                    onView={handleView}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    searchPlaceholder="Search contracts by title, ID, or counterparty..."
                    emptyMessage="No lifecycle records found. Click 'New Contract' to get started."
                  />
                </div>
              )}
            </div>
          )}

          {/* Renewals Tab */}
          {activeTab === 'renewals' && (
            <div className="page-tab-content">
              {loadingRenewals ? (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: '300px',
                    flexDirection: 'column',
                    gap: '16px',
                    backgroundColor: 'white',
                    border: '1px solid var(--border-color)',
                    padding: '40px',
                  }}
                >
                  <div className="loading-spinner"></div>
                  <p
                    style={{
                      color: 'var(--text-secondary)',
                      fontFamily: 'var(--font-family)',
                    }}
                  >
                    Loading renewal alerts...
                  </p>
                </div>
              ) : (
                <div
                  className="page-table-container"
                  style={{
                    backgroundColor: 'white',
                    border: '1px solid var(--border-color)',
                  }}
                >
                  <DataTable
                    data={renewalRows}
                    columns={renewalColumns}
                    title="Pending Contract Renewals"
                    searchable
                    sortable
                    pagination
                    pageSize={10}
                    showActions
                    showCheckboxes={false}
                    onView={(row) =>
                      navigate(`/contracts/view/${row.contractId}`)
                    }
                    customActions={[
                      {
                        type: 'renew',
                        label: 'Mark Renewed',
                        onClick: (row) =>
                          handleRenewalClick(resolveRenewalAlert(row)),
                      },
                    ]}
                    searchPlaceholder="Search renewals by contract ID or counterparty..."
                    emptyMessage="No pending renewals found."
                  />
                </div>
              )}
            </div>
          )}

          {/* Terminations Tab */}
          {activeTab === 'terminations' && (
            <div className="page-tab-content">
              {loadingTerminations ? (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: '300px',
                    flexDirection: 'column',
                    gap: '16px',
                    backgroundColor: 'white',
                    border: '1px solid var(--border-color)',
                    padding: '40px',
                  }}
                >
                  <div className="loading-spinner"></div>
                  <p
                    style={{
                      color: 'var(--text-secondary)',
                      fontFamily: 'var(--font-family)',
                    }}
                  >
                    Loading terminated contracts...
                  </p>
                </div>
              ) : (
                <div
                  className="page-table-container"
                  style={{
                    backgroundColor: 'white',
                    border: '1px solid var(--border-color)',
                  }}
                >
                  <DataTable
                    data={terminationRows}
                    columns={terminationColumns}
                    title="Terminated Contracts"
                    searchable
                    sortable
                    pagination
                    pageSize={10}
                    showActions
                    showCheckboxes={false}
                    onView={(row) =>
                      navigate(`/contracts/view/${row.contractId}`)
                    }
                    searchPlaceholder="Search terminated contracts..."
                    emptyMessage="No terminated contracts found."
                  />
                </div>
              )}
            </div>
          )}

          {/* Analytics Tab */}
          {activeTab === 'analytics' && (
            <div className="page-tab-content">
              {loadingAnalytics ? (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: '300px',
                    flexDirection: 'column',
                    gap: '16px',
                    border: '1px solid var(--border-color)',
                    padding: '40px',
                  }}
                >
                  <div className="loading-spinner"></div>
                  <p
                    style={{
                      color: 'var(--text-secondary)',
                      fontFamily: 'var(--font-family)',
                    }}
                  >
                    Loading analytics...
                  </p>
                </div>
              ) : (
                <div className="flex flex-col gap-6 p-8">
                  {/* TOP STATS */}
                  <div className="grid-modern-4 gap-4">
                    {[
                      {
                        label: 'Renewed Contracts',
                        value: analyticsData?.renewedContracts ?? 0,
                        icon: RefreshCw,
                        iconBg: '#D1FAE5',
                        iconColor: '#059669',
                      },
                      {
                        label: 'Terminated',
                        value: analyticsData?.terminatedContracts ?? 0,
                        icon: X,
                        iconBg: '#FEE2E2',
                        iconColor: '#DC2626',
                      },
                      {
                        label: 'Expiring Soon',
                        value: analyticsData?.expiringSoon ?? 0,
                        icon: Clock,
                        iconBg: '#FEF3C7',
                        iconColor: '#F59E0B',
                      },
                      {
                        label: 'Renewal Rate',
                        value: `${analyticsData?.renewalRate ?? 0}%`,
                        icon: TrendingUp,
                        iconBg: '#D6EFFF',
                        iconColor: '#2D7DD2',
                      },
                      {
                        label: 'Termination Rate',
                        value: `${analyticsData?.terminationRate ?? 0}%`,
                        icon: AlertTriangle,
                        iconBg: '#FEE2E2',
                        iconColor: '#EF4444',
                      },
                      {
                        label: 'Portfolio Value',
                        value: `$${(
                          analyticsData?.totalValue || 0
                        ).toLocaleString()}`,
                        icon: DollarSign,
                        iconBg: '#EDE9FE',
                        iconColor: '#7C3AED',
                      },
                    ].map((card, index) => {
                      const Icon = card.icon;
                      return (
                        <div
                          key={index}
                          className="border border-[var(--border-color)] bg-white"
                        >
                          {/* Accent line */}
                          <div className="h-1 w-full bg-[var(--primary-color)]" />

                          <div className="p-4">
                            {/* ICON | TITLE + VALUE */}
                            <div className="grid grid-cols-[42px_1fr] gap-3 items-center">
                              <div
                                className="h-10 w-10 flex items-center justify-center rounded-lg"
                                style={{
                                  background: card.iconBg,
                                  color: card.iconColor,
                                }}
                              >
                                <Icon size={20} />
                              </div>

                              <div className="leading-tight">
                                <div className="text-sm text-[var(--text-secondary)]">
                                  {card.label}
                                </div>
                                <div className="text-xl font-semibold text-[var(--text-primary)]">
                                  {card.value}
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>

                  {/* BOTTOM LISTS */}
                  <div className="grid-modern-2 gap-6">
                    <div className="border border-[var(--border-color)] bg-white p-4 rounded-2xl">
                      <h5 className="mb-3">Live Status Breakdown</h5>
                      <ul className="list-none p-0 space-y-2">
                        {statusBreakdown.length === 0 ? (
                          <li>No status data yet</li>
                        ) : (
                          statusBreakdown.map(([status, count]) => (
                            <li
                              key={status}
                              className="flex justify-between text-sm"
                            >
                              <span>{status}</span>
                              <strong>{count}</strong>
                            </li>
                          ))
                        )}
                      </ul>
                    </div>

                    <div className="border border-[var(--border-color)] bg-white p-4 rounded-2xl">
                      <h5 className="mb-3">Contract Type Mix</h5>
                      <ul className="list-none p-0 space-y-2">
                        {typeBreakdown.length === 0 ? (
                          <li>No type data available</li>
                        ) : (
                          typeBreakdown.map(([type, count]) => (
                            <li
                              key={type}
                              className="flex justify-between text-sm"
                            >
                              <span>{type}</span>
                              <strong>{count}</strong>
                            </li>
                          ))
                        )}
                      </ul>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Delete Confirmation Modal */}
        <ConfirmationModal
          isOpen={showDeleteModal}
          onClose={() => setShowDeleteModal(false)}
          onConfirm={handleDeleteConfirm}
          title="Delete Contract"
          message={`Are you sure you want to delete contract ${
            selectedContract?.contractId || selectedContract?.id
          }? This action cannot be undone.`}
          confirmText="Delete"
          cancelText="Cancel"
          type="danger"
          isLoading={isDeleting}
        />

        <ConfirmationModal
          isOpen={showRenewalModal}
          onClose={() => {
            setShowRenewalModal(false);
            setActiveRenewal(null);
          }}
          onConfirm={confirmRenewal}
          title="Confirm Renewal"
          message={`Renew contract ${
            activeRenewal?.contract?.contractId ||
            activeRenewal?.contractId ||
            'selected contract'
          }?`}
          confirmText="Renew"
          cancelText="Cancel"
          type="primary"
          isLoading={renewalActionLoading}
        />

        <PageHelp />
        <ToastContainer toasts={toasts} removeToast={removeToast} />
      </div>
    </>
  );
};

export default ContractLifecycle;
